import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Minus, Path, Plus, Square, Tag, Wrench } from '@phosphor-icons/react';
import { aircraftType } from '../data/aircraft';
import { AIRPORTS, AIRPORT_BY_ID } from '../data/airports';
import { COUNTRIES } from '../data/countries';
import type { MultiPolygon, Polygon } from 'geojson';
import type { Airport, GameState } from '../game/types';
import { distanceKm } from '../simulation/math';
import { useI18n } from '../i18n';

const CENTER_LON = 160;
// Adjust this single value to resize airport dots and labels together.
const MAP_MARKER_SCALE = 0.36;
const AI_ROUTE_COLORS = ['#8b5cf6', '#f97316', '#06b6d4', '#ec4899', '#84cc16', '#6366f1', '#14b8a6', '#f59e0b'];
const MAP_DEFAULT_ZOOM = 14;
const MAP_MAX_ZOOM = 22;
const LABEL_PADDING_X = 7;
const LABEL_HEIGHT = 16;
const LABEL_CHAR_WIDTH = 8.6;
const KM_PER_MAP_DEGREE = 111.32;
const PHONE_AIRPORT_AREA_SCALE = Math.SQRT2;

function worldX(lon: number): number {
  return ((lon - CENTER_LON + 540) % 360) - 180;
}

function ringPath(ring: number[][]): string {
  let previous: number | null = null;
  return ring.map(([lon, lat], index) => {
    let x = worldX(lon);
    if (previous !== null) {
      while (x - previous > 180) x -= 360;
      while (x - previous < -180) x += 360;
    }
    previous = x;
    return `${index ? 'L' : 'M'}${x.toFixed(2)},${(-lat).toFixed(2)}`;
  }).join(' ') + ' Z';
}

function polygonPath(polygon: number[][][]): string {
  return polygon.map((ring) => ringPath(ring)).join(' ');
}

function geometryPath(geometry: Polygon | MultiPolygon): string {
  if (geometry.type === 'Polygon') return polygonPath(geometry.coordinates);
  return geometry.coordinates.map((polygon) => polygonPath(polygon)).join(' ');
}

function aiManagerColor(managerId: string): string {
  let hash = 0;
  for (let i = 0; i < managerId.length; i += 1) {
    hash = ((hash << 5) - hash) + managerId.charCodeAt(i);
    hash |= 0;
  }
  return AI_ROUTE_COLORS[Math.abs(hash) % AI_ROUTE_COLORS.length];
}

function routePath(originLon: number, originLat: number, destinationLon: number, destinationLat: number): string {
  const x1 = worldX(originLon);
  let x2 = worldX(destinationLon);
  while (x2 - x1 > 180) x2 -= 360;
  while (x2 - x1 < -180) x2 += 360;
  const y1 = -originLat;
  const y2 = -destinationLat;
  const distance = Math.hypot(x2 - x1, y2 - y1);
  const curve = Math.min(14, distance * 0.13);
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2 - curve;
  return `M${x1},${y1} Q${mx},${my} ${x2},${y2}`;
}

interface Camera { x: number; y: number; zoom: number }
interface Rect { left: number; right: number; top: number; bottom: number }
interface LabelBox extends Rect { value: number }
interface LabelPlacement { x: number; y: number }

function boxesOverlap(first: Rect, second: Rect): boolean {
  return first.left < second.right && first.right > second.left && first.top < second.bottom && first.bottom > second.top;
}

export function visibleAirportRightBoundary(left: number, right: number, phoneViewport: boolean): number {
  return phoneViewport ? right : left + (right - left) * 0.6;
}

export function cameraForAirport(airport: Pick<Airport, 'lon' | 'lat'>): Camera {
  return { x: Number(worldX(airport.lon).toFixed(2)), y: -airport.lat, zoom: 18 };
}

export interface WorldMapProps {
  game: GameState;
  selectedAirportId?: string;
  selectedRouteId?: string;
  selectedAiRouteId?: string;
  focusAirportId?: string;
  focusAirportNonce?: number;
  routeOriginId?: string;
  planningAircraftId?: string;
  onAirportSelect: (iata: string) => void;
  onRouteSelect: (routeId: string) => void;
  onAiRouteSelect?: (managerId: string, routeId: string) => void;
  showBorders?: boolean;
  performanceMode?: boolean;
  availableAirportIds?: ReadonlySet<string>;
}

export function WorldMap({ game, selectedAirportId, selectedRouteId, selectedAiRouteId, focusAirportId, focusAirportNonce, routeOriginId, planningAircraftId, onAirportSelect, onRouteSelect, onAiRouteSelect, showBorders = true, performanceMode = false, availableAirportIds }: WorldMapProps) {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const airportsRef = useRef<SVGGElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const moved = useRef(false);
  const [aspect, setAspect] = useState(1.8);
  const [phoneViewport, setPhoneViewport] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [showAllAirportLabels, setShowAllAirportLabels] = useState(false);
  const [showAirportList, setShowAirportList] = useState(false);
  const [showMyRoutes, setShowMyRoutes] = useState(true);
  const [cameraTick, setCameraTick] = useState(0);
  const aspectRef = useRef(aspect);
  const cameraRef = useRef<Camera>({ x: -38, y: -18, zoom: MAP_DEFAULT_ZOOM });
  const showAirportListRef = useRef(false);
  const frameRef = useRef<number | null>(null);
  const effectiveShowBorders = showBorders && !performanceMode;
  const landPaths = useMemo(() => COUNTRIES.flatMap((country) => {
    const geometry = country.feature.geometry;
    return geometry.type === 'Polygon' ? [polygonPath(geometry.coordinates)] : geometry.coordinates.map(polygonPath);
  }), []);
  const countryPaths = useMemo(() => COUNTRIES.map((country) => ({ id: country.id, name: country.name, d: geometryPath(country.feature.geometry) })), []);
  const activeRoutes = useMemo(() => game.routes.filter((route) => route.active), [game.routes]);
  const hubs = useMemo(() => new Set(game.hubs.map((hub) => hub.airportId)), [game.hubs]);
  const connected = useMemo(() => new Set(activeRoutes.flatMap((route) => [route.originId, route.destinationId])), [activeRoutes]);
  const availableAirports = useMemo(() => availableAirportIds ? AIRPORTS.filter((airport) => availableAirportIds.has(airport.iata)) : AIRPORTS, [availableAirportIds]);
  const routeOrigin = useMemo(() => (routeOriginId ? AIRPORT_BY_ID.get(routeOriginId) : undefined), [routeOriginId]);
  const idleAircraftTypes = useMemo(() => game.aircraft
    .filter((aircraft) => aircraft.routeIds.length === 0)
    .filter((aircraft) => !planningAircraftId || aircraft.id === planningAircraftId)
    .map((aircraft) => aircraftType(aircraft.typeId)), [game.aircraft, planningAircraftId]);
  const planningRangeKm = useMemo(() => (routeOrigin
    ? Math.max(0, ...idleAircraftTypes
      .filter((type) => type.runwayClass <= routeOrigin.runwayClass)
      .map((type) => type.rangeKm))
    : 0), [idleAircraftTypes, routeOrigin]);
  const reachableAirports = useMemo(() => new Set(routeOrigin && planningRangeKm
    ? availableAirports
      .filter((airport) => airport.iata !== routeOrigin.iata)
      .filter((airport) => idleAircraftTypes.some((type) => (
        type.runwayClass <= routeOrigin.runwayClass
        && type.runwayClass <= airport.runwayClass
        && distanceKm(routeOrigin, airport) <= type.rangeKm
      )))
      .map((airport) => airport.iata)
    : []), [availableAirports, idleAircraftTypes, planningRangeKm, routeOrigin]);
  const renderedAirports = useMemo(() => {
    const airportLayerOrder = (iata: string) => (
      selectedAirportId === iata ? 0
        : hubs.has(iata) ? 3
          : connected.has(iata) ? 2
            : 1
    );
    return [...availableAirports].sort((a, b) => airportLayerOrder(a.iata) - airportLayerOrder(b.iata));
  }, [availableAirports, connected, hubs, selectedAirportId]);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      const nextAspect = entry.contentRect.width / Math.max(1, entry.contentRect.height);
      aspectRef.current = nextAspect;
      setAspect(nextAspect);
      applyCamera(cameraRef.current);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!window.matchMedia) return;
    const query = window.matchMedia('(max-width: 560px)');
    const syncPhoneViewport = () => setPhoneViewport(query.matches);
    syncPhoneViewport();
    query.addEventListener('change', syncPhoneViewport);
    return () => query.removeEventListener('change', syncPhoneViewport);
  }, []);

  useEffect(() => {
    showAirportListRef.current = showAirportList;
  }, [showAirportList]);

  // 滚轮缩放需要 preventDefault，React 根节点上的 wheel 监听是被动的，
  // 因此在 svg 上以非被动方式手动绑定，避免控制台警告且保证页面不滚动。
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const handleWheel = (event: globalThis.WheelEvent) => {
      event.preventDefault();
      zoomBy(event.deltaY > 0 ? 0.88 : 1.14);
    };
    svg.addEventListener('wheel', handleWheel, { passive: false });
    return () => svg.removeEventListener('wheel', handleWheel);
  }, []);

  function applyCamera(cam: Camera) {
    if (!svgRef.current) return;
    const viewWidth = 360 / cam.zoom;
    const viewHeight = viewWidth / aspectRef.current;
    svgRef.current.setAttribute('viewBox', `${cam.x - viewWidth / 2} ${cam.y - viewHeight / 2} ${viewWidth} ${viewHeight}`);
    if (showAirportListRef.current) setCameraTick((tick) => tick + 1);
  }

  useEffect(() => {
    if (!focusAirportId) return;
    const airport = AIRPORT_BY_ID.get(focusAirportId);
    if (!airport) return;
    cameraRef.current = cameraForAirport(airport);
    applyCamera(cameraRef.current);
    applyMarkerScale(cameraRef.current.zoom);
    setCameraTick((tick) => tick + 1);
  }, [focusAirportId, focusAirportNonce]);

  function applyMarkerScale(zoom: number) {
    if (!airportsRef.current) return;
    const zoomBoost = zoom <= MAP_DEFAULT_ZOOM ? 1 : 1 + (zoom - MAP_DEFAULT_ZOOM) / (MAP_MAX_ZOOM - MAP_DEFAULT_ZOOM);
    const markerScale = (MAP_MARKER_SCALE / zoom) * zoomBoost;
    airportsRef.current.style.setProperty('--marker-scale', markerScale.toFixed(4));
  }

  function scheduleApplyCamera() {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      applyCamera(cameraRef.current);
      applyMarkerScale(cameraRef.current.zoom);
    });
  }

  const viewWidth = 360 / cameraRef.current.zoom;
  const viewHeight = viewWidth / aspect;
  const viewBox = `${cameraRef.current.x - viewWidth / 2} ${cameraRef.current.y - viewHeight / 2} ${viewWidth} ${viewHeight}`;
  const zoomBoost = cameraRef.current.zoom <= MAP_DEFAULT_ZOOM ? 1 : 1 + (cameraRef.current.zoom - MAP_DEFAULT_ZOOM) / (MAP_MAX_ZOOM - MAP_DEFAULT_ZOOM);
  const markerScale = (MAP_MARKER_SCALE / cameraRef.current.zoom) * zoomBoost;
  const visibleAirports = useMemo(() => {
    void cameraTick;
    const cam = cameraRef.current;
    const width = 360 / cam.zoom;
    const height = width / aspectRef.current;
    const left = cam.x - width / 2;
    const right = cam.x + width / 2;
    const visibleRight = visibleAirportRightBoundary(left, right, phoneViewport);
    const top = cam.y - height / 2;
    const bottom = cam.y + height / 2;
    const filtered = availableAirports.filter((airport) => {
      const y = -airport.lat;
      if (y < top || y > bottom) return false;
      const x = worldX(airport.lon);
      return [x, x - 360, x + 360].some((candidateX) => candidateX >= left && candidateX <= visibleRight);
    });
    if (routeOrigin) {
      return filtered
        .filter((airport) => airport.iata !== routeOrigin.iata)
        .sort((leftAirport, rightAirport) => {
          const leftConnected = connected.has(leftAirport.iata);
          const rightConnected = connected.has(rightAirport.iata);
          if (leftConnected !== rightConnected) return Number(leftConnected) - Number(rightConnected);
          return distanceKm(routeOrigin, rightAirport) - distanceKm(routeOrigin, leftAirport);
        });
    }
    return filtered.sort((leftAirport, rightAirport) => (
      Number(hubs.has(rightAirport.iata)) - Number(hubs.has(leftAirport.iata))
      || Number(connected.has(rightAirport.iata)) - Number(connected.has(leftAirport.iata))
      || rightAirport.market - leftAirport.market
      || leftAirport.iata.localeCompare(rightAirport.iata)
    ));
  }, [availableAirports, cameraTick, connected, hubs, aspect, phoneViewport, routeOrigin]);
  const airportLabels = useMemo(() => {
    const airportValue = (iata: string) => (
      selectedAirportId === iata ? 1000
        : hubs.has(iata) ? 820
          : connected.has(iata) ? 640
            : reachableAirports.has(iata) ? 460
              : (AIRPORT_BY_ID.get(iata)?.market ?? 1) * 36
    );
    const radiusScale = phoneViewport ? PHONE_AIRPORT_AREA_SCALE : 1;
    const airportDots = renderedAirports.map((airport) => {
      const baseRadius = hubs.has(airport.iata) ? 5.3 : connected.has(airport.iata) ? 4.2 : 3.1;
      const dotRadius = baseRadius * radiusScale * markerScale;
      const x = worldX(airport.lon);
      const y = -airport.lat;
      return { iata: airport.iata, value: airportValue(airport.iata), box: { left: x - dotRadius, right: x + dotRadius, top: y - dotRadius, bottom: y + dotRadius } };
    });
    const placed: LabelBox[] = [];
    const placements = new Map<string, LabelPlacement>();
    const candidates = renderedAirports
      .filter((airport) => showAllAirportLabels || selectedAirportId === airport.iata)
      .sort((left, right) => airportValue(right.iata) - airportValue(left.iata));

    for (const airport of candidates) {
      const value = airportValue(airport.iata);
      const baseRadius = hubs.has(airport.iata) ? 5.3 : connected.has(airport.iata) ? 4.2 : 3.1;
      const dotRadius = baseRadius * radiusScale;
      const labelWidth = airport.nameZh.length * LABEL_CHAR_WIDTH + LABEL_PADDING_X * 2;
      const x = worldX(airport.lon);
      const y = -airport.lat;
      const offsets: LabelPlacement[] = [
        { x: dotRadius + 5, y: 0 },
        { x: dotRadius + 5, y: -LABEL_HEIGHT - 2 },
        { x: dotRadius + 5, y: LABEL_HEIGHT + 2 },
        { x: -labelWidth - dotRadius - 5, y: 0 },
        { x: -labelWidth - dotRadius - 5, y: -LABEL_HEIGHT - 2 },
        { x: -labelWidth - dotRadius - 5, y: LABEL_HEIGHT + 2 },
        { x: -labelWidth / 2, y: -LABEL_HEIGHT - dotRadius - 5 },
        { x: -labelWidth / 2, y: LABEL_HEIGHT + dotRadius + 5 },
      ];

      for (const offset of offsets) {
        const box: LabelBox = {
          left: x + offset.x * markerScale,
          right: x + (offset.x + labelWidth) * markerScale,
          top: y + (offset.y - LABEL_HEIGHT / 2) * markerScale,
          bottom: y + (offset.y + LABEL_HEIGHT / 2) * markerScale,
          value,
        };
        const hitsPlacedLabel = placed.some((existing) => boxesOverlap(box, existing));
        const hitsHigherAirport = airportDots.some((dot) => dot.iata !== airport.iata && dot.value >= value && boxesOverlap(box, dot.box));
        if (!hitsPlacedLabel && !hitsHigherAirport) {
          placed.push(box);
          placements.set(airport.iata, offset);
          break;
        }
      }
    }
    return placements;
  }, [connected, hubs, markerScale, phoneViewport, reachableAirports, renderedAirports, selectedAirportId, showAllAirportLabels]);

  function zoomBy(multiplier: number) {
    cameraRef.current = { ...cameraRef.current, zoom: Math.max(1, Math.min(MAP_MAX_ZOOM, cameraRef.current.zoom * multiplier)) };
    applyCamera(cameraRef.current);
    applyMarkerScale(cameraRef.current.zoom);
  }

  function onPointerDown(event: ReactPointerEvent<SVGSVGElement>) {
    // 机场/航线/对手航线上按下时不捕获指针：桌面端捕获会把 click 重定向到 svg，导致无法选中；
    // 不捕获时 pointermove 仍会冒泡到 svg，拖动平移不受影响
    if (!(event.target as Element).closest('.airport, .route, .ai-route')) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    moved.current = false;
  }

  function onAirportPointerDown() {
    moved.current = false;
  }

  function onPointerMove(event: ReactPointerEvent<SVGSVGElement>) {
    const previous = pointers.current.get(event.pointerId);
    if (!previous) return;
    const allBefore = [...pointers.current.values()];
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const allAfter = [...pointers.current.values()];
    if (Math.hypot(event.clientX - previous.x, event.clientY - previous.y) > 2) moved.current = true;

    if (allAfter.length === 1 && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const currentViewWidth = 360 / cameraRef.current.zoom;
      const currentViewHeight = currentViewWidth / aspectRef.current;
      const dx = (event.clientX - previous.x) / rect.width * currentViewWidth;
      const dy = (event.clientY - previous.y) / rect.height * currentViewHeight;
      cameraRef.current = { ...cameraRef.current, x: cameraRef.current.x - dx, y: Math.max(-82, Math.min(82, cameraRef.current.y - dy)) };
      scheduleApplyCamera();
    } else if (allAfter.length === 2 && allBefore.length === 2) {
      const beforeDistance = Math.hypot(allBefore[0].x - allBefore[1].x, allBefore[0].y - allBefore[1].y);
      const afterDistance = Math.hypot(allAfter[0].x - allAfter[1].x, allAfter[0].y - allAfter[1].y);
      if (beforeDistance > 0 && afterDistance > 0) {
        cameraRef.current = { ...cameraRef.current, zoom: Math.max(1, Math.min(MAP_MAX_ZOOM, cameraRef.current.zoom * (afterDistance / beforeDistance))) };
        scheduleApplyCamera();
      }
    }
  }

  function onPointerUp(event: ReactPointerEvent<SVGSVGElement>) {
    pointers.current.delete(event.pointerId);
  }

  return (
    <div className="map-container" ref={containerRef}>
      <svg
        ref={svgRef}
        className={`world-map ${routeOriginId ? 'route-planning' : ''} ${performanceMode ? 'performance-mode' : ''}`}
        viewBox={viewBox}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="application"
        aria-label="世界航空网络地图"
      >
        <rect x={cameraRef.current.x - viewWidth} y={cameraRef.current.y - viewHeight} width={viewWidth * 2} height={viewHeight * 2} className="ocean" />
        <g className="graticule">
          {[-120, -60, 0, 60, 120].map((x) => <line key={`x-${x}`} x1={x} y1={-85} x2={x} y2={85} />)}
          {[-60, -30, 0, 30, 60].map((lat) => <line key={`y-${lat}`} x1={-540} y1={-lat} x2={540} y2={-lat} />)}
        </g>
        <g className="land">
          {[-360, 0, 360].flatMap((offset) => landPaths.map((path, index) => (
            <path key={`${offset}-${index}`} d={path} transform={`translate(${offset} 0)`} />
          )))}
        </g>
        {effectiveShowBorders && (
          <g className="country-borders">
            {[-360, 0, 360].flatMap((offset) => countryPaths.map((country, index) => (
              <path
                key={`b-${offset}-${index}`}
                d={country.d}
                transform={`translate(${offset} 0)`}
              >
                <title>{country.name}</title>
              </path>
            )))}
          </g>
        )}
        <g className="ai-routes">
          {game.aiManagers?.filter((manager) => !manager.bankrupt).flatMap((manager) => {
            const color = aiManagerColor(manager.id);
            return manager.routes
              .filter((route) => route.active)
              .map((route) => {
                const origin = AIRPORT_BY_ID.get(route.originId);
                const destination = AIRPORT_BY_ID.get(route.destinationId);
                if (!origin || !destination) return null;
                const path = routePath(origin.lon, origin.lat, destination.lon, destination.lat);
                const selected = selectedAiRouteId === `${manager.id}:${route.id}`;
                return (
                  <g
                    key={`ai-${manager.id}-${route.id}`}
                    className={`ai-route ${selected ? 'selected' : ''}`}
                    onClick={(event) => { event.stopPropagation(); if (!moved.current) onAiRouteSelect?.(manager.id, route.id); }}
                  >
                    <path className="ai-route-hit" d={path} vectorEffect="non-scaling-stroke" />
                    <path
                      className="ai-route-line"
                      d={path}
                      stroke={color}
                      strokeWidth={selected ? 2.5 : 1}
                      strokeDasharray="4 4"
                      vectorEffect="non-scaling-stroke"
                      fill="none"
                    />
                  </g>
                );
              });
          })}
        </g>
        <g className={`routes ${showMyRoutes ? '' : 'hidden'}`}>
          {showMyRoutes && activeRoutes.map((route) => {
            const origin = AIRPORT_BY_ID.get(route.originId)!;
            const destination = AIRPORT_BY_ID.get(route.destinationId)!;
            const color = route.stats.operatingProfit < 0 ? 'var(--red)' : route.stats.loadFactor > 0.92 ? 'var(--gold)' : 'var(--green)';
            const width = 1.5 + Math.min(4, route.stats.seats / 2200);
            const path = routePath(origin.lon, origin.lat, destination.lon, destination.lat);
            return (
              <g key={route.id} className={selectedRouteId === route.id ? 'route selected' : 'route'} onClick={(event) => { event.stopPropagation(); if (!moved.current) onRouteSelect(route.id); }}>
                <path className="route-hit" d={path} vectorEffect="non-scaling-stroke" />
                <path className="route-glow" d={path} stroke={color} strokeWidth={width + 2.5} vectorEffect="non-scaling-stroke" />
                <path className="route-line" d={path} stroke={color} strokeWidth={width} vectorEffect="non-scaling-stroke" />
              </g>
            );
          })}
        </g>
        {routeOrigin && planningRangeKm > 0 && (
          <g className="range-guides" transform={`translate(${worldX(routeOrigin.lon)} ${-routeOrigin.lat})`}>
            <circle className="range-circle" r={planningRangeKm / KM_PER_MAP_DEGREE} />
          </g>
        )}
        <g className="airports" ref={airportsRef} style={{ ['--marker-scale' as any]: markerScale }}>
          {renderedAirports.map((airport) => {
            const x = worldX(airport.lon);
            const y = -airport.lat;
            const selected = selectedAirportId === airport.iata;
            const isHub = hubs.has(airport.iata);
            const isConnected = connected.has(airport.iata);
            const isReachable = reachableAirports.has(airport.iata);
            const labelPlacement = airportLabels.get(airport.iata);
            const labelVisible = labelPlacement !== undefined;
            const baseRadius = isHub ? 5.3 : isConnected ? 4.2 : 3.1;
            const radiusScale = phoneViewport ? PHONE_AIRPORT_AREA_SCALE : 1;
            const dotRadius = baseRadius * radiusScale;
            const touchRadius = (baseRadius + 3) * radiusScale;
            const labelWidth = airport.nameZh.length * LABEL_CHAR_WIDTH + LABEL_PADDING_X * 2;
            const labelX = labelPlacement?.x ?? dotRadius + 5;
            const labelY = labelPlacement?.y ?? 0;
            return (
              <g
                key={airport.iata}
                data-airport-id={airport.iata}
                className={`airport ${selected ? 'selected' : ''} ${isHub ? 'hub' : ''} ${isConnected ? 'connected' : ''}`}
                transform={`translate(${x} ${y})`}
                onPointerDown={onAirportPointerDown}
                onClick={(event) => { event.stopPropagation(); if (!moved.current) onAirportSelect(airport.iata); }}
              >
                <g className="airport-marker">
                  <circle className="airport-touch-target" r={touchRadius} />
                  {selected && !performanceMode && <circle className="airport-pulse" r={9} />}
                  <circle className="airport-dot" r={dotRadius} />
                  {isHub && <circle className="hub-ring" r={8.2 * radiusScale} />}
                  {isReachable && <circle className="airport-reachable-ring" r={(baseRadius + 1) * radiusScale} />}
                  {labelVisible && (
                    <g className="airport-label-group" transform="translate(0 0)">
                      <rect className="airport-label-bg" x={labelX} y={labelY - LABEL_HEIGHT / 2} rx="4" width={labelWidth} height={LABEL_HEIGHT} />
                      <text className="airport-label" x={labelX + LABEL_PADDING_X} y={labelY} dominantBaseline="middle">{airport.nameZh}</text>
                    </g>
                  )}
                </g>
              </g>
            );
          })}
        </g>
      </svg>
      <div className="map-controls" aria-label={t('ui.地图控制')}>
        <button onClick={() => zoomBy(1.25)} aria-label={t('ui.放大')}><Plus weight="bold" size={22} /></button>
        <button onClick={() => zoomBy(0.8)} aria-label={t('ui.缩小')}><Minus weight="bold" size={22} /></button>
        <div className="map-tool-wrap">
          <button onClick={() => setToolsOpen((open) => !open)} aria-label={t('ui.地图工具')} aria-expanded={toolsOpen}><Wrench weight="bold" size={20} /></button>
          {toolsOpen && (
            <div className="map-tool-menu" role="menu" aria-label={t('ui.地图工具菜单')}>
              <button className={showAllAirportLabels ? 'active' : ''} onClick={() => setShowAllAirportLabels((visible) => !visible)} role="menuitem" aria-label={t('ui.显示全部标签')} title={t('ui.显示全部标签')}>
                <Tag weight="bold" size={16} />
              </button>
              <button className={showMyRoutes ? 'active' : ''} onClick={() => setShowMyRoutes((visible) => !visible)} role="menuitem" aria-label={t('ui.显示我的航线')} title={t('ui.显示我的航线')}>
                <Path weight="bold" size={16} />
              </button>
              <button className={showAirportList ? 'active' : ''} onClick={() => setShowAirportList((visible) => !visible)} role="menuitem" aria-label={t('ui.显示视野机场')} title={t('ui.显示视野机场')}>
                <Square weight="bold" size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
      {showAirportList && (
        <aside className="visible-airport-panel" aria-label={t('ui.视野内机场')}>
          <header>
            <span>{t('ui.视野内机场')}</span>
            <strong>{visibleAirports.length}</strong>
          </header>
          <div className="visible-airport-list">
            {visibleAirports.map((airport) => {
              const isHub = hubs.has(airport.iata);
              const isConnected = connected.has(airport.iata);
              const planningDistance = routeOrigin ? `${Math.round(distanceKm(routeOrigin, airport)).toLocaleString()} km` : undefined;
              return (
                <button key={airport.iata} onClick={() => onAirportSelect(airport.iata)}>
                  <span>
                    <span className="airport-name-row">
                      <strong>{airport.nameZh}</strong>
                    </span>
                    <small>
                      <span className="airport-meta">{airport.iata} · {airport.region}</span>
                      {planningDistance && <span className="airport-distance">{planningDistance}</span>}
                    </small>
                  </span>
                  <b>{isHub ? t('ui.枢纽') : isConnected ? t('ui.已通航') : t('ui.市场 {market}', { market: airport.market })}</b>
                </button>
              );
            })}
          </div>
        </aside>
      )}
      {routeOriginId && <div className="route-mode-banner">{t('ui.从 {origin} 出发 · 点击目的地', { origin: routeOriginId })}</div>}
      <div className="map-legend">
        <span><i className="legend-profit" />{t('ui.盈利')}</span>
        <span><i className="legend-tight" />{t('ui.满载')}</span>
        <span><i className="legend-loss" />{t('ui.亏损')}</span>
      </div>
    </div>
  );
}
